import { createContextFactory, useIntl } from '@mfw/components';
import { ContentTypeOptions } from '@mfw/common';
import { v4 as uuidv4 } from 'uuid';
import omit from 'lodash/omit';
import React from 'react';

import type {
  UploadWorkerUpdateProgressArgs,
  UploadWorkerDequeArgs,
  UploadWorkerQueArgs,
  UploadWorkerTask,
} from './types';
import type {
  UiQueCreateExercise,
  UiQueEditExercise,
} from '../../schemas/exercises.schema';

import { useEditExerciseById, useCreateExercise } from '../../hooks/exercises';
import { useSetPremiumItem } from '../../hooks/premiumContent';
import { useOrganizationContext } from '../OrganizationProvider';
import { useAccountScopeReset } from '../accountScope';
import { UploadWorkerTaskEvent } from './types';
import { staleChunkRecovery } from '../../allFeatures/AppVersion/staleChunkRecoveryInstance';
import { useUploadWorker } from './useUploadWorker';

export type UploadWorkerContextType = {
  /**
   * Starts HTTP request and adds task to the queue.
   */
  queCreateTask: (meta: UploadWorkerQueArgs) => UploadWorkerTask;
  getTaskById: (taskId: string) => UploadWorkerTask | undefined;
  queEditTask: (meta: UploadWorkerQueArgs) => UploadWorkerTask;
  /**
   * Stops HTTP request and removes task by id.
   */
  dequeTask: ({ id }: UploadWorkerDequeArgs) => void;
  tasks: UploadWorkerTask[];
};

const UploadWorkerContext = createContextFactory({
  devToolsName: 'UploadWorkerContext',
  initialValue: {} as UploadWorkerContextType,
});

export interface UploadWorkerProviderProps {
  children?: React.ReactNode;
}

const UploadWorkerProvider = ({ children }: UploadWorkerProviderProps) => {
  const [tasks, setTasks] = React.useState<UploadWorkerTask[]>([]);
  const { locale } = useIntl();
  const { currentOrganization } = useOrganizationContext();

  /**
   * THE ACCOUNT BOUNDARY (PR-2.1). The queue and the worker used to be safe by accident —
   * arriving as someone else meant crossing `/auth ↔ /`, which remounted this provider.
   * An identity can now move with nothing unmounting, and what is queued here is the
   * previous account's files going into the previous account's organization.
   */
  const { accountEpoch } = useAccountScopeReset(() => setTasks([]));
  /**
   * …and the same answer for code that is no longer rendering. An upload has a TAIL: the
   * worker finishes the media PUT, posts back, and only then does the provider create the
   * exercise and register it as premium. That tail is an async function already holding a
   * task, so emptying the list above cannot stop it — it has to ask, after every await,
   * whether the account it started under is still the one signed in. The EPOCH answers
   * that in one comparison and is immune to the cold boot, where the key itself moves once
   * as the tenant and then the account settle. Written in render rather than from an
   * effect on purpose: a settling promise must never find this ref a commit behind and
   * take one more write.
   */
  const accountEpochRef = React.useRef(accountEpoch);
  accountEpochRef.current = accountEpoch;

  const { mutateAsync: createExerciseAsync } = useCreateExercise();
  const { mutateAsync: editExerciseAsync } = useEditExerciseById();
  const { mutateAsync: setPremiumItemAsync } = useSetPremiumItem();

  const getTaskById = React.useCallback(
    (taskId: string) => {
      return tasks.find((task) => task.id === taskId);
    },
    [tasks],
  );

  const completeCreateTask = React.useCallback(
    async (id: string) => {
      const task = getTaskById(id);
      if (!task) return;
      // Whose upload this is. Everything after an await is checked against it — one
      // mutation of the previous account's content is one too many.
      const owner = accountEpochRef.current;
      const isStillOwner = () => accountEpochRef.current === owner;
      try {
        const created = await createExerciseAsync({
          ...(omit(
            task.meta,
            'organizationName',
            'isPremium',
          ) as UiQueCreateExercise),
          locale,
        });
        if (!isStillOwner()) return;
        // premium-content: an exercise created from the /premium flow is flagged
        // in the task meta; its id is only known here (after the worker upload +
        // create), so we register it as premium at this point.
        if ('isPremium' in task.meta && task.meta.isPremium && created?.id) {
          await setPremiumItemAsync({
            contentId: created.id,
            contentType: ContentTypeOptions.EXERCISE,
            enabled: true,
          });
          if (!isStillOwner()) return;
        }
        setTasks((prev) =>
          prev.map((task) =>
            task.id === id ? { ...task, progress: 100, status: 'done' } : task,
          ),
        );
      } catch {
        if (!isStillOwner()) return;
        setTasks((prev) =>
          prev.map((task) =>
            // TODO We can add message from error here and show it in UI. This should apply for 422 errors, which should not happen but still...
            task.id === id ? { ...task, progress: 100, status: 'error' } : task,
          ),
        );
      }
    },
    [createExerciseAsync, locale, getTaskById, setPremiumItemAsync],
  );

  const completeEditTask = React.useCallback(
    async (id: string) => {
      const task = getTaskById(id);
      if (!task) return;
      const owner = accountEpochRef.current;
      const isStillOwner = () => accountEpochRef.current === owner;
      try {
        await editExerciseAsync({
          ...(omit(task.meta, 'organizationName') as UiQueEditExercise),
          locale,
        });
        if (!isStillOwner()) return;
        setTasks((prev) =>
          prev.map((task) =>
            task.id === id ? { ...task, progress: 100, status: 'done' } : task,
          ),
        );
      } catch {
        if (!isStillOwner()) return;
        setTasks((prev) =>
          prev.map((task) =>
            // TODO We can add message from error here and show it in UI. This should apply for 422 errors, which should not happen but still...
            task.id === id ? { ...task, progress: 100, status: 'error' } : task,
          ),
        );
      }
    },
    [editExerciseAsync, getTaskById, locale],
  );

  /**
   * Removes task from list.
   */
  const removeTask = React.useCallback(
    (id: string) => {
      setTasks((prev) =>
        prev.filter((task) => {
          return task.id !== id;
        }),
      );
    },
    [setTasks],
  );

  /**
   * Marks a task the worker could not finish.
   *
   * The worker has always been able to post `OnError` — for a failed cover, and
   * now for a failed media PUT as well — and nothing has ever listened for it.
   * A failed upload therefore sat in the list at whatever percentage it reached
   * when the network gave out: no error, no completion, no removal, and no way
   * for the user to tell it apart from one still running.
   *
   * `'error'` rather than removing the row, matching what `completeCreateTask`
   * and `completeEditTask` already do when the API call fails — the user has to
   * see that the work did not happen.
   */
  const failTask = React.useCallback(
    (id: string) => {
      setTasks((prev) =>
        prev.map((task) =>
          task.id === id ? { ...task, status: 'error' } : task,
        ),
      );
    },
    [setTasks],
  );

  /**
   * Updates task progress internally.
   */
  const updateTaskProgress = React.useCallback(
    (id: string, progress: number) => {
      setTasks((prev) =>
        prev.map((task) => (task.id === id ? { ...task, progress } : task)),
      );
    },
    [setTasks],
  );

  const onWorkerMessage = React.useCallback(
    (event: MessageEvent) => {
      const { type } = event.data;
      if (type === UploadWorkerTaskEvent.OnReady) {
        staleChunkRecovery.onWorkerReady();
      } else if (type === UploadWorkerTaskEvent.OnCreateExerciseCompleted) {
        const payload = event.data.payload as UploadWorkerTask;
        completeCreateTask(payload.id);
      } else if (type === UploadWorkerTaskEvent.OnEditExerciseCompleted) {
        const payload = event.data.payload as UploadWorkerTask;
        completeEditTask(payload.id);
      } else if (type === UploadWorkerTaskEvent.OnError) {
        const payload = event.data.payload as UploadWorkerTask;
        failTask(payload.id);
      } else if (type === UploadWorkerTaskEvent.OnStopped) {
        const payload = event.data.payload as UploadWorkerDequeArgs;
        removeTask(payload.id);
      } else if (type === UploadWorkerTaskEvent.OnProgress) {
        const payload = event.data.payload as UploadWorkerUpdateProgressArgs;
        updateTaskProgress(payload.id, payload.progress);
      }
    },
    [
      completeCreateTask,
      completeEditTask,
      failTask,
      removeTask,
      updateTaskProgress,
    ],
  );

  // V2-30: a worker holding a pre-deploy manifest asks `importScripts` for a
  // vendor chunk the deploy deleted, and fails inside the worker where the
  // route-level chunk guard cannot see it. Same one-shot reload, its own
  // guard — released by `OnReady` above, never by a route import (sen-5 F6).
  // sen-8 U5: the worker's OWN script 404ing arrives as a plain `Event` with
  // no `message`; the recovery tells that apart from a runtime error by
  // whether `OnReady` ever arrived.
  const onWorkerError = React.useCallback((event: Event) => {
    staleChunkRecovery.onWorkerError(event);
  }, []);

  // Each Worker starts unproven: this provider is mounted per route branch, so
  // a page constructs more than one over its life (sen-8 U5).
  const onWorkerStart = React.useCallback(() => {
    staleChunkRecovery.onWorkerStarted();
  }, []);

  const worker = useUploadWorker({
    onError: onWorkerError,
    onMessage: onWorkerMessage,
    onStart: onWorkerStart,
    // One worker per account, not per mount: a worker that outlived the account that
    // started it is still uploading their files, and its completions would arrive here
    // under someone else's session. The epoch, not the key — a cold boot settles the key
    // once and that is not a new account.
    ownerKey: accountEpoch,
  });

  /**
   * Starts HTTP request and adds task to the queue.
   */
  const queCreateTask = React.useCallback(
    ({ meta }: UploadWorkerQueArgs) => {
      const filteredOrganizationIds = meta.organizationIds.filter(
        (organization) => organization.value !== meta.organizationId,
      );
      // For each organization id we create a copy and separate task + default org id which is sent
      if (filteredOrganizationIds.length > 0) {
        filteredOrganizationIds.forEach((organization) => {
          const task = {
            id: uuidv4(),
            locale,
            meta: {
              ...meta,
              organizationId: organization.value,
              organizationName: organization.label,
            },
            progress: 0,
            status: 'uploading',
          } as UploadWorkerTask;
          setTasks((prev) => [...prev, task]);
          worker?.postMessage({
            payload: task,
            type: UploadWorkerTaskEvent.StartCreateUpload,
          });
        });
      }

      const task = {
        id: uuidv4(),
        locale,
        meta: {
          ...meta,
          organizationName: currentOrganization?.name,
        },
        progress: 0,
        status: 'uploading',
      } as UploadWorkerTask;
      setTasks((prev) => [...prev, task]);
      worker?.postMessage({
        payload: task,
        type: UploadWorkerTaskEvent.StartCreateUpload,
      });
      return task;
    },
    [currentOrganization?.name, locale, worker],
  );

  const queEditTask = React.useCallback(
    ({ meta }: UploadWorkerQueArgs) => {
      const task = {
        id: uuidv4(),
        locale,
        meta: {
          ...meta,
          organizationName: currentOrganization?.name,
        },
        progress: 0,
        status: 'uploading',
      } as UploadWorkerTask;
      setTasks((prev) => [...prev, task]);
      worker?.postMessage({
        payload: task,
        type: UploadWorkerTaskEvent.StartEditUpload,
      });
      return task;
    },
    [currentOrganization?.name, locale, worker],
  );

  /**
   * Stops HTTP request and removes task by id.
   */
  const dequeTask = React.useCallback(
    ({ id }: UploadWorkerDequeArgs) => {
      worker?.postMessage({
        payload: { id },
        type: UploadWorkerTaskEvent.StopUpload,
      });
    },
    [worker],
  );

  const providedValue = React.useMemo(
    () => ({ dequeTask, getTaskById, queCreateTask, queEditTask, tasks }),
    [queCreateTask, dequeTask, queEditTask, getTaskById, tasks],
  );

  return (
    <UploadWorkerContext.Provider value={providedValue}>
      {children}
    </UploadWorkerContext.Provider>
  );
};

export function useUploadWorkerContext() {
  return UploadWorkerContext.useContext();
}

export default UploadWorkerProvider;
